// Create + edit customer in a single modal. Same form, different mutation.

import { useEffect } from 'react';
import { Form, Input, Modal, Switch, message } from 'antd';
import type { Customer, CreateCustomerInput } from './customers.api';
import { useCreateCustomer, useUpdateCustomer } from './useCustomers';

interface Props {
  open: boolean;
  editing: Customer | null;
  onClose: () => void;
}

export function CustomerFormModal({ open, editing, onClose }: Props) {
  const [form] = Form.useForm<CreateCustomerInput>();
  const create = useCreateCustomer();
  const update = useUpdateCustomer();

  useEffect(() => {
    if (open) {
      if (editing) form.setFieldsValue(editing);
      else form.resetFields();
    }
  }, [open, editing, form]);

  async function handleOk() {
    try {
      const values = await form.validateFields();
      if (editing) {
        await update.mutateAsync({ id: editing.id, input: values });
      } else {
        await create.mutateAsync(values);
      }
      message.success('Đã lưu');
      onClose();
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      if (apiMessage) message.error(apiMessage);
    }
  }

  return (
    <Modal
      open={open}
      title={editing ? 'Sửa customer' : 'Tạo customer mới'}
      onCancel={onClose}
      onOk={() => void handleOk()}
      confirmLoading={create.isPending || update.isPending}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false} initialValues={{ isActive: true }}>
        <Form.Item name="name" label="Tên công ty" rules={[{ required: true }]}>
          <Input maxLength={200} autoFocus />
        </Form.Item>
        <Form.Item name="contactName" label="Người liên hệ" rules={[{ required: true }]}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="contactEmail" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="contactPhone" label="Điện thoại">
          <Input maxLength={30} />
        </Form.Item>
        <Form.Item name="companyTaxId" label="MST">
          <Input allowClear />
        </Form.Item>
        <Form.Item name="notes" label="Ghi chú">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="isActive" label="Hoạt động" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
